/**
 * URL allow-list and block-list for browser fetch/XHR OpenTelemetry instrumentation.
 *
 * Only {@code /api/} and {@code /realtime-api/} paths (relative or same-origin absolute) may produce spans.
 * High-volume or low-value routes are excluded via block-list patterns.
 */

const escapeForRegex = ( value: string ): string => {
    return value.replace( /[-/\\^$*+?.()|[\]{}]/g, "\\$&" );
};

/**
 * Regex matchers for FlowRide backend API prefixes used for trace header propagation
 * and as the instrumentation allow-list perimeter.
 *
 * @returns Matchers for relative paths and same-origin absolute URLs, or empty when not in a browser.
 */
export const buildFlowRideInstrumentedUrlMatchers = (): RegExp[] => {
    if ( typeof window === "undefined" ) {
        return [];
    }

    const escapedOrigin = escapeForRegex( window.location.origin );

    return [
        /^\/api\//,
        /^\/realtime-api\//,
        new RegExp( `^${ escapedOrigin }/api/` ),
        new RegExp( `^${ escapedOrigin }/realtime-api/` ),
    ];
};

/**
 * Block-list patterns applied only after {@link isFlowRideInstrumentedApiUrl} matches.
 * Prevents OTLP feedback loops and noisy spans on geocode, logs, realtime polling, etc.
 */
export const OTEL_BROWSER_BLOCKLIST_URL_PATTERNS: ReadonlyArray<RegExp> = [
    /\/api\/observability\/otlp\//,
    /\/api\/logs(?:\/|$)/,
    /\/api\/geocode(?:\/|\?|$)/,
    /^\/realtime-api\//,
    /\/api\/stops(?:\/|\?|$)/,
    /\/api\/errors\/report(?:\/|\?|$)/,
];

let blocklistWithOriginCache: RegExp[] | null = null;

/**
 * Block-list including same-origin absolute {@code /realtime-api/} paths.
 *
 * @returns Patterns including origin-scoped realtime-api matcher when in a browser.
 */
const getBlocklistPatterns = (): ReadonlyArray<RegExp> => {
    if ( typeof window === "undefined" ) {
        return OTEL_BROWSER_BLOCKLIST_URL_PATTERNS;
    }

    if ( blocklistWithOriginCache === null ) {
        const escapedOrigin = escapeForRegex( window.location.origin );
        blocklistWithOriginCache = [
            ...OTEL_BROWSER_BLOCKLIST_URL_PATTERNS,
            new RegExp( `^${ escapedOrigin }/realtime-api/` ),
        ];
    }

    return blocklistWithOriginCache;
};

/**
 * Patterns that skip instrumentation for traffic outside the FlowRide API allow-list.
 *
 * @returns Regexes for cross-origin requests and same-origin non-API paths, or empty on SSR.
 */
const buildNonFlowRideApiIgnorePatterns = (): RegExp[] => {
    if ( typeof window === "undefined" ) {
        return [];
    }

    const escapedOrigin = escapeForRegex( window.location.origin );

    return [
        new RegExp( `^(?!${ escapedOrigin })https?://` ),
        new RegExp( `^${ escapedOrigin }/(?!api/|realtime-api/)` ),
        /^\/assets\//,
        /^\/@/,
        /^\/node_modules\//,
    ];
};

/**
 * @param url Request URL string from fetch/XHR instrumentation (relative or absolute).
 * @returns True when the URL is under {@code /api/} or {@code /realtime-api/} (FlowRide backend perimeter).
 */
export const isFlowRideInstrumentedApiUrl = ( url: string ): boolean => {
    const matchers = buildFlowRideInstrumentedUrlMatchers();

    return matchers.some( ( pattern ) => pattern.test( url ) );
};

const matchesBlocklist = ( url: string ): boolean => {
    return getBlocklistPatterns().some( ( pattern ) => pattern.test( url ) );
};

/**
 * @param url Request URL from fetch/XHR instrumentation.
 * @returns True when instrumentation should be skipped (no span, no export for this request).
 */
export const shouldIgnoreUrlForBrowserTracing = ( url: string ): boolean => {
    if ( !isFlowRideInstrumentedApiUrl( url ) ) {
        return true;
    }

    return matchesBlocklist( url );
};

/**
 * Combined ignore patterns for {@link FetchInstrumentation} and {@link XMLHttpRequestInstrumentation}
 * ({@code ignoreUrls} accepts only strings and regexes, not predicate functions).
 *
 * @returns Patterns equivalent to {@link shouldIgnoreUrlForBrowserTracing}.
 */
export const buildOpenTelemetryIgnoreUrlPatterns = (): Array<string | RegExp> => {
    return [
        ...buildNonFlowRideApiIgnorePatterns(),
        ...getBlocklistPatterns(),
    ];
};
